import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ChevronRight, ArrowLeft, Check, Sparkles, Mail, Phone } from "lucide-react";
import { api, StorageKeys } from "../lib/api";
import { storage } from "../lib/storage";
import { useI18n } from "../lib/i18n";
import { useSEO } from "../lib/seo";

const AGE_GROUPS = ["<18", "18-25", "26-35", "36-50", "51-65", "65+"];

// ==========================================
// ⏱️ SAVE DELAY (data is being saved to Sangat DB)
// ==========================================
const TEST_DELAY_SECONDS = 10;

// ==========================================
// ✨ SIKH GK THOUGHTS — auto-spin while saving
// ==========================================
const GK_THOUGHTS = {
  en: [
    "Guru Nanak Dev Ji, the first Guru, founded Sikhi in 1469.",
    "Sri Guru Granth Sahib Ji is the eternal, living Guru of the Sikhs.",
    "“Ik Onkar” means there is only One Creator of all.",
    "Sri Harmandir Sahib has four doors — welcoming people from every direction.",
    "Langar, the free kitchen begun by Guru Nanak Dev Ji, feeds all as equals.",
    "There were ten human Gurus; Guru Gobind Singh Ji was the tenth.",
  ],
  hi: [
    "पहले गुरु, गुरु नानक देव जी ने 1469 में सिखी की नींव रखी।",
    "श्री गुरु ग्रंथ साहिब जी सिखों के शाश्वत, जीवंत गुरु हैं।",
    "“इक ਓਂਕਾਰ” का अर्थ है — सबका रचयिता केवल एक है।",
    "श्री हरिमंदिर साहिब के चार द्वार हैं — हर दिशा से सबका स्वागत।",
    "लंगर, गुरु नानक देव जी द्वारा शुरू किया गया मुफ़्त भोजन, सबको समान मानता है।",
    "दस मानव गुरु हुए; गुरु गोबिंद सिंह जी दसवें गुरु थे।",
  ],
  pa: [
    "ਪਹਿਲੇ ਗੁਰੂ, ਗੁਰੂ ਨਾਨਕ ਦੇਵ ਜੀ ਨੇ 1469 ਵਿੱਚ ਸਿੱਖੀ ਦੀ ਨੀਂਹ ਰੱਖੀ।",
    "ਸ੍ਰੀ ਗੁਰੂ ਗ੍ਰੰਥ ਸਾਹਿਬ ਜੀ ਸਿੱਖਾਂ ਦੇ ਸਦੀਵੀ, ਜਿਊਂਦੇ ਗੁਰੂ ਹਨ।",
    "“ਇੱਕ ਓਅੰਕਾਰ” ਦਾ ਅਰਥ ਹੈ — ਸਭ ਦਾ ਸਿਰਜਣਹਾਰ ਕੇਵਲ ਇੱਕ ਹੈ।",
    "ਸ੍ਰੀ ਹਰਿਮੰਦਰ ਸਾਹਿਬ ਦੇ ਚਾਰ ਦਰਵਾਜ਼ੇ ਹਨ — ਹਰ ਦਿਸ਼ਾ ਤੋਂ ਸਭ ਦਾ ਸੁਆਗਤ।",
    "ਲੰਗਰ, ਗੁਰੂ ਨਾਨਕ ਦੇਵ ਜੀ ਵੱਲੋਂ ਸ਼ੁਰੂ ਕੀਤਾ ਮੁਫ਼ਤ ਭੋਜਨ, ਸਭ ਨੂੰ ਬਰਾਬਰ ਮੰਨਦਾ ਹੈ।",
    "ਦਸ ਮਨੁੱਖੀ ਗੁਰੂ ਹੋਏ; ਗੁਰੂ ਗੋਬਿੰਦ ਸਿੰਘ ਜੀ ਦਸਵੇਂ ਗੁਰੂ ਸਨ।",
  ],
};

const SAVING_COPY = {
  en: {
    title: "Please wait ~10 seconds",
    sub: "We are securely saving your details to the GurSetu Sangat…",
    tag: "Did you know?",
  },
  hi: {
    title: "कृपया ~10 सेकंड प्रतीक्षा करें",
    sub: "हम आपकी जानकारी GurSetu संगत में सुरक्षित रूप से सेव कर रहे हैं…",
    tag: "क्या आप जानते हैं?",
  },
  pa: {
    title: "ਕਿਰਪਾ ਕਰਕੇ ~10 ਸਕਿੰਟ ਉਡੀਕੋ",
    sub: "ਅਸੀਂ ਤੁਹਾਡੀ ਜਾਣਕਾਰੀ GurSetu ਸੰਗਤ ਵਿੱਚ ਸੁਰੱਖਿਅਤ ਸੇਵ ਕਰ ਰਹੇ ਹਾਂ…",
    tag: "ਕੀ ਤੁਹਾਨੂੰ ਪਤਾ ਹੈ?",
  },
};

function SavingOverlay({ lang }) {
  const facts = GK_THOUGHTS[lang] || GK_THOUGHTS.en;
  const copy = SAVING_COPY[lang] || SAVING_COPY.en;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIdx((p) => (p + 1) % facts.length);
    }, 2600);
    return () => clearInterval(id);
  }, [facts.length]);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[2147483646] grid place-items-center px-6"
      style={{
        background:
          "radial-gradient(700px 480px at 50% 0%, rgba(212,175,55,0.14), transparent 60%), rgba(6,7,10,0.92)",
      }}
    >
      <div className="w-full max-w-sm text-center">
        <div className="relative mx-auto h-24 w-24">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
            className="absolute -inset-1 rounded-full"
            style={{
              background:
                "conic-gradient(from 0deg, transparent, rgba(245,184,58,0.95), transparent, rgba(245,184,58,0.35), transparent)",
            }}
          />
          <div className="absolute inset-0 grid place-items-center rounded-full border border-amber-400/30 bg-[#0b0f16]">
            <span className="text-3xl text-amber-300">ੴ</span>
          </div>
        </div>

        <h2 className="mt-6 font-heading text-xl font-black text-[#f7f1e7]">{copy.title}</h2>
        <p className="mx-auto mt-1.5 max-w-[300px] text-[12px] font-medium leading-relaxed text-white/60">{copy.sub}</p>

        <div className="mx-auto mt-5 h-1.5 w-52 overflow-hidden rounded-full bg-white/10">
          <motion.div
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: TEST_DELAY_SECONDS, ease: "linear" }}
            className="h-full rounded-full"
            style={{ background: "linear-gradient(90deg,#ffe58d,#f4c842,#ddb323)" }}
          />
        </div>

        <div className="mx-auto mt-7 min-h-[132px] w-full rounded-[22px] border border-amber-400/15 bg-amber-400/[0.05] px-5 py-5">
          <p className="text-[9px] font-black uppercase tracking-[2px] text-amber-400">{copy.tag}</p>
          <div className="relative mt-2 grid min-h-[74px] place-items-center overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.p
                key={idx}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.5 }}
                className="text-[13px] font-semibold leading-relaxed text-white/85"
              >
                {facts[idx]}
              </motion.p>
            </AnimatePresence>
          </div>
          <div className="mt-3 flex items-center justify-center gap-1.5">
            {facts.map((_, i) => (
              <span
                key={i}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: i === idx ? 18 : 6,
                  background: i === idx ? "#f4c842" : "rgba(255,255,255,0.22)",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>,
    document.body
  );
}

function Particles() {
  const dots = [
    { top: "16%", left: "12%", s: 5, d: 0 }, { top: "22%", left: "84%", s: 4, d: 0.8 },
    { top: "58%", left: "8%", s: 6, d: 1.4 }, { top: "70%", left: "80%", s: 4, d: 0.5 },
    { top: "40%", left: "90%", s: 3, d: 1 }, { top: "82%", left: "38%", s: 5, d: 1.8 },
    { top: "12%", left: "50%", s: 3, d: 0.3 }, { top: "48%", left: "20%", s: 4, d: 2.2 },
  ];
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {dots.map((d, i) => (
        <span key={i} className="particle absolute rounded-full" style={{ top: d.top, left: d.left, width: d.s, height: d.s, background: "var(--gold)", boxShadow: "0 0 10px var(--gold)", animationDelay: `${d.d}s` }} />
      ))}
    </div>
  );
}

function Progress({ step, total }) {
  return (
    <div className="flex gap-1.5 mb-6">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${i <= step ? "gold-gradient" : "bg-white/10"}`} />
      ))}
    </div>
  );
}

// ==========================================
// 🛡️ FRAUD PREVENTION VALIDATION
// ==========================================
const isValidEmail = (email) => {
  if (!email) return true;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const isValidPhone = (phone) => {
  if (!phone) return true;
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 7 || digits.length > 15) return false;
  if (/^(.)\1{6,}$/.test(digits)) return false;
  if (/^0+$/.test(digits)) return false;
  return /^\d+$/.test(digits);
};

export default function Onboarding() {
  const navigate = useNavigate();
  const { t, lang, setLang, LANGS } = useI18n();
  useSEO({ title: "Welcome to GurSetu", description: "Join the GurSetu Sangat.", path: "/onboarding" });
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ fullName: "", email: "", city: "", country: "India", phone: "", ageGroup: "", occupation: "" });
  const [busy, setBusy] = useState(false);

  const inp = "w-full bg-[var(--glass-light)] border border-[var(--glass-border)] rounded-2xl px-4 py-3.5 text-sm text-text outline-none focus:border-[var(--gold-subtle)] transition-colors placeholder:text-slate-500";

  const submit = async () => {
    if (!form.fullName.trim() || !form.city.trim()) {
      toast.warning("Please fill in your Name and City");
      return;
    }
    const hasEmail = form.email.trim().length > 0;
    const hasPhone = form.phone.trim().length > 0;
    if (!hasEmail && !hasPhone) {
      toast.warning("Please provide either your Email or Phone Number (ANY ONE) so we can keep you updated!");
      return;
    }
    if (hasEmail && !isValidEmail(form.email)) {
      toast.warning("Please enter a valid Email address (e.g., name@example.com).");
      return;
    }
    if (hasPhone && !isValidPhone(form.phone)) {
      toast.warning("Please enter a valid Phone Number. Fake or repeating numbers are not allowed.");
      return;
    }

    setBusy(true);
    try {
      if (TEST_DELAY_SECONDS > 0) {
        await new Promise((resolve) => setTimeout(resolve, TEST_DELAY_SECONDS * 1000));
      }
      const r = await api.post("/sangat/register", { ...form, language: lang });
      storage.set(StorageKeys.UserId, r.userId);
      storage.set(StorageKeys.UserProfile, form);
      storage.set(StorageKeys.OnboardingComplete, true);
      setStep(3);
    } catch (e) {
      toast.error("Could not register. Please check your details.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-app grid place-items-center p-5 sm:p-6 relative overflow-hidden">
      <AnimatePresence>{busy && <SavingOverlay lang={lang} />}</AnimatePresence>

      <div className="fixed inset-0 pointer-events-none" style={{ background: "radial-gradient(900px 600px at 50% 0%, rgba(212,175,55,0.16), transparent 60%), linear-gradient(180deg, #1a1408, var(--bg))" }} />
      <Particles />

      <div className="w-full max-w-md relative z-10">
        <AnimatePresence mode="wait">
          {/* =========================================================
              STEP 0: WELCOME  (✅ CLEAN — no wordmark component, no infinite loops)
          ========================================================= */}
          {step === 0 && (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, y: -18, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              className="relative overflow-hidden rounded-[32px] border border-amber-400/25 bg-[#0b0a06] px-5 pb-7 pt-6 text-center shadow-[0_28px_90px_rgba(0,0,0,0.58)] sm:px-7 sm:pb-8"
            >
              {/* static soft glows — NO animation (mobile-safe) */}
              <div className="pointer-events-none absolute left-1/2 top-8 h-44 w-64 -translate-x-1/2 rounded-full bg-amber-400/10 blur-[70px]" />
              <div className="pointer-events-none absolute -bottom-24 -right-20 h-56 w-56 rounded-full bg-orange-400/[0.06] blur-[90px]" />

              <motion.div
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
                className="absolute right-4 top-4 z-20 inline-flex items-center gap-1.5 rounded-full border border-amber-400/35 bg-amber-400/[0.08] px-3 py-1.5 text-[8px] font-black uppercase tracking-[1.7px] text-amber-300"
              >
                <Sparkles size={10} /> {t("required")}
              </motion.div>

              {/* 🙏 — one-time entry only */}
              <motion.div
                initial={{ opacity: 0, scale: 0.6, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 0.15, type: "spring", stiffness: 260 }}
                className="relative z-10 mt-6 text-[44px] leading-none"
              >
                🙏
              </motion.div>

              {/* ✅ STATIC WORDMARK — clean text, zero infinite animations */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.45 }}
                className="relative z-10 mx-auto mt-2 max-w-[290px] rounded-[34px] border border-amber-400/10 bg-gradient-to-b from-amber-400/[0.08] via-amber-300/[0.045] to-transparent px-5 py-6"
              >
                <div className="flex flex-col items-center">
                  <span className="text-2xl font-bold text-amber-400">ੴ</span>
                  <span
                    className="mt-1 text-[40px] sm:text-[46px] font-black leading-tight tracking-tight text-amber-400"
                    style={{ textShadow: "0 2px 22px rgba(212,175,55,0.35)" }}
                  >
                    GurSetu
                  </span>
                  <div className="mt-2 h-px w-28 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="relative z-10 mt-3">
                <p className="text-[15px] font-extrabold tracking-[0.2px] text-[#f7f1e7]">{t("fateh")}</p>
                <p className="mt-0.5 text-[15px] font-extrabold text-amber-400">{t("fatehReply")}</p>
              </motion.div>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }} className="relative z-10 mx-auto mt-4 flex items-center justify-center gap-2">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-400/45" />
                <span className="text-[9px] text-amber-400/65">ੴ</span>
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-400/45" />
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }} className="relative z-10 mx-auto mt-4 max-w-[320px]">
                <p className="text-[13px] font-medium leading-[1.75] text-white/65">
                  Join the <span className="font-bold text-amber-300">GurSetu Sangat</span> to receive today's Hukamnama, Gurpurab reminders and a more personal spiritual journey.
                </p>
                <div className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full border border-amber-400/15 bg-amber-400/[0.06] px-3.5 py-2">
                  <span className="h-2 w-2 rounded-full bg-amber-400" />
                  <p className="text-[10px] font-bold text-amber-200/85">Just 30 seconds — then GurSetu feels truly yours.</p>
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }} className="relative z-10 mt-5 grid grid-cols-3 gap-2">
                {[
                  { icon: "ੴ", title: "Daily", text: "Hukamnama" },
                  { icon: "✨", title: "Many More", text: "Personal spiritual features" },
                  { icon: "🙏", title: "Join", text: "Sangat" },
                ].map((item) => (
                  <div key={item.title} className="rounded-[16px] border border-white/[0.055] bg-white/[0.025] px-2 py-3 text-center">
                    <div className="text-[17px]">{item.icon}</div>
                    <p className="mt-1 text-[9px] font-black uppercase tracking-[0.8px] text-white/75">{item.title}</p>
                    <p className="mt-0.5 text-[8px] text-white/40">{item.text}</p>
                  </div>
                ))}
              </motion.div>

              <motion.button
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85 }}
                whileTap={{ scale: 0.985 }}
                onClick={() => setStep(1)}
                data-testid="onb-continue"
                className="relative z-10 mt-6 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#ffe58d] via-[#f4c842] to-[#ddb323] px-5 text-[14px] font-black text-[#171005] shadow-[0_10px_30px_rgba(228,179,48,0.20)]"
              >
                <span className="relative z-10">{t("continue")}</span>
                <span className="relative z-10 text-amber-950/40">·</span>
                <span className="relative z-10 font-gurmukhi">ਅੱਗੇ ਵਧੋ</span>
                <ChevronRight size={17} className="relative z-10" />
              </motion.button>

              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.95 }} className="relative z-10 mt-3 text-[9px] font-medium text-white/35">
                Takes less than a minute · Helps personalize your GurSetu
              </motion.p>
            </motion.div>
          )}

          {/* =========================================================
              STEP 1: LANGUAGE
          ========================================================= */}
          {step === 1 && (
            <motion.div key="lang" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.4 }} className="glass rounded-[32px] p-7">
              <Progress step={0} total={3} />
              <h2 className="font-heading text-2xl font-bold text-text">{t("chooseLanguage")}</h2>
              <p className="text-dim text-sm mb-5">{t("Complete your profile for a more personal GurSetu experience")}</p>
              <div className="space-y-2.5">
                {LANGS && LANGS.map((l) => (
                  <button key={l} onClick={() => setLang(l)} data-testid={`onb-lang-${l}`}
                    className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl border transition-all ${lang === l ? "border-[var(--gold)] bg-[rgba(212,175,55,0.12)]" : "border-[var(--glass-border)] hover:border-[var(--gold-subtle)]"}`}>
                    <span className={`text-base ${lang === l ? "text-gold font-semibold" : "text-text"}`}>
                      {l === "en" ? "English" : l === "hi" ? "हिंदी" : l === "pa" ? "ਪੰਜਾਬੀ" : l}
                    </span>
                    {lang === l && <span className="w-6 h-6 rounded-full gold-gradient grid place-items-center"><Check size={14} className="text-black" /></span>}
                  </button>
                ))}
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(0)} className="btn-ghost px-5 py-3.5 flex items-center gap-1" data-testid="onb-back"><ArrowLeft size={16} />{t("back")}</button>
                <button onClick={() => setStep(2)} className="btn-gold flex-1 py-3.5 flex items-center justify-center gap-2" data-testid="onb-lang-continue">{t("continue")} <ChevronRight size={18} /></button>
              </div>
            </motion.div>
          )}

          {/* =========================================================
              STEP 2: DETAILS
          ========================================================= */}
          {step === 2 && (
            <motion.div key="details" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.4 }} className="glass rounded-[32px] p-7">
              <Progress step={1} total={3} />
              <h2 className="font-heading text-2xl font-bold text-text">{t("regTitle")}</h2>
              <p className="text-dim text-sm mb-5">{t("Complete your profile & make GurSetu truly yours")}</p>

              <div className="mb-6 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-amber-300 mb-1">Stay Connected & Be the First to Know! 🌟</p>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      To ensure you never miss a sacred Hukamnama, exclusive GurSetu updates, or personalized spiritual reminders, please share either your Email or Phone Number(ANY ONE). We deeply value your privacy and only use this to bring the divine experience directly to you.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Field label={t("fullName") + " *"}>
                  <input className={inp} value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} data-testid="onb-fullName" placeholder="Your Full Name" />
                </Field>

                <Field label={t("city") + " *"}>
                  <input className={inp} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="onb-city" placeholder="Your City" />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Email">
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input type="email" className={`${inp} pl-9`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="onb-email" placeholder="name@example.com" />
                    </div>
                  </Field>
                  <Field label="Phone (Write country code also)">
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input type="tel" className={`${inp} pl-9`} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="onb-phone" placeholder="+91XXXXXXXXXX" />
                    </div>
                  </Field>
                </div>

                <Field label={t("country")}>
                  <input className={inp} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="onb-country" />
                </Field>

                <Field label={t("ageGroup")}>
                  <div className="flex flex-wrap gap-2">
                    {AGE_GROUPS.map((a) => (
                      <button key={a} onClick={() => setForm({ ...form, ageGroup: form.ageGroup === a ? "" : a })} data-testid={`onb-age-${a}`}
                        className={`px-3 py-2 rounded-full text-xs border transition-all ${form.ageGroup === a ? "gold-gradient text-black font-semibold border-transparent" : "border-[var(--glass-border)] text-dim hover:border-amber-500/50"}`}>
                        {a}
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label={t("occupation")}>
                  <input className={inp} value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} data-testid="onb-occupation" placeholder="Your Occupation(OPTIONAL)" />
                </Field>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(1)} className="btn-ghost px-5 py-3.5 flex items-center gap-1" data-testid="onb-details-back"><ArrowLeft size={16} />{t("back")}</button>
                <button onClick={submit} disabled={busy} className="btn-gold flex-1 py-3.5 disabled:opacity-50" data-testid="onb-finish">{busy ? "…" : t("finish")}</button>
              </div>
            </motion.div>
          )}

          {/* =========================================================
              STEP 3: COMPLETE
          ========================================================= */}
          {step === 3 && (
            <motion.div key="complete" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="glass rounded-[32px] p-8 text-center">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.2 }} className="text-6xl mb-4">🙏</motion.div>
              <h1 className="font-heading text-3xl font-extrabold text-text">{t("Welcome To GurSetu")}</h1>
              <p className="text-dim text-sm mt-3 leading-relaxed">{t(" Thoughtfully crafted by Codesphere Technologies to bring faith, reflection, and meaningful digital experiences closer to you. ")}</p>
              <div className="flex flex-wrap gap-2 justify-center mt-6">
                {[t("todaysHukamnama"), t("simran"), t("ai"), t("Many Personal spiritual features")].map((p, i) => (
                  <motion.span key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.1 }}
                    className="px-4 py-2 rounded-full border border-[var(--gold-subtle)] text-gold text-xs">{p}</motion.span>
                ))}
              </div>
              <button onClick={() => navigate("/")} className="btn-gold w-full py-4 mt-8 text-base" data-testid="onb-enter">{t("Begin My Day😇")}</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wide text-gold font-semibold block mb-1.5">{label}</label>
      {children}
    </div>
  );
}